import type {
  HeadlineResponsePersonBaseRelatedPagedData,
  PersonInfluencerResponse,
  PlayerOrgRatingAllResponsePagedData,
  PlayerProfileResponse,
  PlayerRatingResponse,
  PlayerVisitsRes,
  ProfileRecruitResponse,
  PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData,
  PublicMeasurementPersonResponseRecord,
  RecruitmentEvaluationResponsePagedData,
  RpmSummaryResponse,
  TeamTargetResponsePagedData,
  VideoResponsePagedData,
} from '@on3/api';
import { type IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { getIdFromParam, getSportKeyFromParam } from '@on3/ui-lib/utils/url';
import { AthleteClaimModal } from 'components/Db/Profile/AthleteClaim/AthleteClaimModal';
import { PlayerRecruiting } from 'components/Db/Profile/PlayerRecruiting';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { AthleteProvider } from 'contexts/AthleteProvider';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { createDescription, createTitle } from 'utils/meta';
import {
  type Athlete,
  AthleteType,
  getAthleteType,
  getSportKeyFromPage,
  playerModules,
} from 'utils/player';

interface IPlayerRecruitingProps extends IGenericPageProps {
  basicInfo: PlayerProfileResponse;
  playerProfileData?: Athlete['profile'];
  rankingsAll?: PlayerOrgRatingAllResponsePagedData | null;
  rankingsPlayer?: PlayerRatingResponse | null;
  recruitData?: ProfileRecruitResponse | null;
  recruitsArticles?: HeadlineResponsePersonBaseRelatedPagedData | null;
  rpmSummary?: RpmSummaryResponse | null;
  scoutingJournals?: RecruitmentEvaluationResponsePagedData | null;
  socialData?: PersonInfluencerResponse[] | null;
  sportKey?: number;
  teamTargets?: TeamTargetResponsePagedData | null;
  videoData?: VideoResponsePagedData | null;
  visitCenter?: PlayerVisitsRes | null;
  vitalAverages?: PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData | null;
  vitals?: PublicMeasurementPersonResponseRecord | null;
}

const PlayerRecruitingPage = ({
  basicInfo,
  socialData,
  videoData,
  teamTargets,
  rpmSummary,
  recruitsArticles,
  visitCenter,
  playerProfileData,
  rankingsAll,
  rankingsPlayer,
  scoutingJournals,
  vitalAverages,
  vitals,
  ...props
}: IPlayerRecruitingProps) => {
  const data = useMemo(() => {
    return { athleteData: basicInfo, socialData };
  }, [basicInfo, socialData]);

  const title = createTitle(basicInfo?.name, '- Recruiting Profile');
  const description = createDescription(
    'Latest recruiting data from ',
    basicInfo?.name,
  );
  const { query } = useRouter();
  const { sport } = query;
  const keySport = (sport as string) ?? 'default';

  const image = useMemo(() => {
    return {
      height: basicInfo?.defaultAsset?.height || 200,
      src:
        basicInfo?.defaultAsset &&
        `https://${basicInfo?.defaultAsset?.domain}${basicInfo?.defaultAsset?.source}`,
      width: basicInfo?.defaultAsset?.width || 200,
    };
  }, [basicInfo?.defaultAsset]);

  return (
    <AthleteProvider data={data}>
      <AthleteClaimModal />
      <PageWrapper
        {...props}
        description={description}
        image={image}
        ogType="athlete"
        title={title}
      >
        <PlayerRecruiting
          key={keySport}
          rankingsAll={rankingsAll}
          rankingsPlayer={rankingsPlayer}
          recruitsArticles={recruitsArticles}
          scoutingJournals={scoutingJournals}
          teamTargets={teamTargets}
          videoData={videoData}
          visitCenter={visitCenter}
          vitalAverages={vitalAverages}
          vitals={vitals}
        />
      </PageWrapper>
    </AthleteProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { id, sport } = ctx.query as {
    id: string;
    sport?: string;
  };

  const playerId = getIdFromParam(id as string);
  let sportKey = getSportKeyFromParam(sport?.toLowerCase() ?? '') ?? undefined;

  if (!playerId) return { notFound: true };

  try {
    const [player] = await Promise.allSettled([
      playerModules.player(proxyApi, { personKey: playerId, sportKey }),
    ]);

    const fetchedPlayer = getFulfilledValue(player);

    if (!fetchedPlayer) return { notFound: true };

    //This will ensure we are always are on the correct url and there won't be any duplication.
    if (id !== fetchedPlayer.slug) {
      return {
        redirect: {
          permanent: true,
          destination: `/db/${fetchedPlayer.slug}/recruiting/`,
        },
      };
    }

    if (!fetchedPlayer.isAthlete && fetchedPlayer.isCoach) {
      return {
        notFound: true,
      };
    }

    sportKey = getSportKeyFromPage(fetchedPlayer, sport);
    const playerType = getAthleteType(fetchedPlayer as Athlete);

    // Redirect to the player profile page if the sport is not supported for recruiting
    // Redirect away from recruting if player is in High School
    // This will need to look at base once implmented
    if (![1, 2, 5, 12].includes(sportKey) || playerType === AthleteType.Star) {
      return {
        redirect: {
          destination: `/db/${fetchedPlayer.slug}/`,
          permanent: true,
        },
      };
    }

    const personKey = fetchedPlayer.key;

    const [
      recruitArticles,
      visitCenter,
      videoData,
      teamTargets,
      rankingsAll,
      rankingsPlayer,
      scoutingJournals,
      vitalAverages,
      vitals,
    ] = await Promise.allSettled([
      playerModules.news(proxyApi, { key: personKey, limit: 5 }),
      playerModules.visits(proxyApi, { playerKey: personKey }),
      playerModules.videos(proxyApi, { personKey }),
      playerModules.topTeams(proxyApi, { playerKey: personKey, sportKey }),
      playerModules.rankings.all(proxyApi, { personKey, sportKey }),
      playerModules.rankings.player(proxyApi, { personKey, sportKey }),
      playerModules.scouting.journals(proxyApi, { personKey }),
      playerModules.vitalAverages(proxyApi, { personKey }),
      playerModules.vitals(proxyApi, { personKey }),
    ]);

    const data = {
      pageType: 'database',
      siteData,
      userData: userData || {},
      basicInfo: fetchedPlayer,
      recruitsArticles: getFulfilledValue(recruitArticles),
      visitCenter: getFulfilledValue(visitCenter),
      videoData: getFulfilledValue(videoData),
      teamTargets: getFulfilledValue(teamTargets),
      rankingsAll: getFulfilledValue(rankingsAll),
      rankingsPlayer: getFulfilledValue(rankingsPlayer),
      scoutingJournals: getFulfilledValue(scoutingJournals),
      vitalAverages: getFulfilledValue(vitalAverages),
      vitals: getFulfilledValue(vitals),
    };

    return { props: data };
  } catch (e) {
    console.log(e);

    return { props: [] };
  }
};

export default withValidUrl(PlayerRecruitingPage);
